export interface BankCredit {
	id: string;
	description: string;
	amountCents: number;
	occurredOn: string;
	direction: "DEBIT" | "CREDIT";
	bankStatus: "POSTED" | "PENDING";
	paymentMethod: string | null;
	payerName: string | null;
	payerTaxId: string | null;
}

export interface GuardianReceivableSettlement {
	id: string;
	settledOn: string;
	amountCents: number;
	customerId: number;
	customerName: string;
	customerTaxId: string | null;
	titleNumber: string;
	issuedOn: string | null;
	dueOn: string | null;
	paymentMethod: string | null;
}

type ReceiptStatus = "RECONCILED" | "AWAITING_GUARDIAN" | "REVIEW_REQUIRED" | "NOT_RECEIVABLE";

type MatchKind = "ONE_TO_ONE" | "BANK_GROUP" | "GUARDIAN_GROUP";

export interface ReceiptGroup {
	id: string;
	status: ReceiptStatus;
	matchKind: MatchKind | null;
	reason:
		| "INTERNAL_TRANSFER"
		| "INVESTMENT_MOVEMENT"
		| "CHECK_DEPOSIT"
		| "MULTIPLE_GUARDIAN_COMBINATIONS"
		| "NO_GUARDIAN_SETTLEMENT"
		| null;
	occurredOn: string;
	amountCents: number;
	bankTransactions: BankCredit[];
	settlements: GuardianReceivableSettlement[];
}

export interface ReceiptMovementClassification {
	transactionId: string;
	explanation: string;
	destination: string;
	basis: "GUARDIAN_RECONCILIATION" | "BANK_DESCRIPTION_RULE";
}

export const Receipts = {
	reconcile(bankTransactions: BankCredit[], guardianSettlements: GuardianReceivableSettlement[]) {
		const credits = bankTransactions.filter(
			(transaction) => transaction.direction === "CREDIT" && transaction.bankStatus === "POSTED",
		);
		const groups: ReceiptGroup[] = [];
		const availableCredits = new Map(credits.map((credit) => [credit.id, credit]));
		const availableSettlements = new Map(
			guardianSettlements.map((settlement) => [settlement.id, settlement]),
		);

		for (const credit of credits) {
			const reason = nonReceivableReason(credit.description);

			if (reason) {
				groups.push(unmatchedGroup(credit, "NOT_RECEIVABLE", reason));
				availableCredits.delete(credit.id);
				continue;
			}

			if (/^DEP CHEQUE\b/u.test(credit.description.trim())) {
				groups.push(unmatchedGroup(credit, "REVIEW_REQUIRED", "CHECK_DEPOSIT"));
				availableCredits.delete(credit.id);
			}
		}

		const creditsByKey = Map.groupBy(availableCredits.values(), matchKey);
		const settlementsByKey = Map.groupBy(availableSettlements.values(), (settlement) =>
			matchKey({ occurredOn: settlement.settledOn, amountCents: settlement.amountCents }),
		);

		for (const [key, matchingCredits] of creditsByKey) {
			const matchingSettlements = settlementsByKey.get(key) ?? [];

			if (
				matchingSettlements.length === 0 ||
				matchingCredits.length !== matchingSettlements.length
			) {
				continue;
			}

			groups.push(
				reconciledGroup(
					matchingCredits,
					matchingSettlements,
					matchingCredits.length === 1 ? "ONE_TO_ONE" : "BANK_GROUP",
				),
			);
			removeMatched(matchingCredits, matchingSettlements);
		}

		const ambiguousCredits = new Set<string>();

		for (const credit of [...availableCredits.values()].toSorted(compareCredits)) {
			const candidates = [...availableSettlements.values()].filter(
				(settlement) =>
					settlement.settledOn === credit.occurredOn && settlement.paymentMethod !== "CH",
			);
			const subsets = matchingSubsets(candidates, credit.amountCents);

			if (subsets.length > 1) {
				ambiguousCredits.add(credit.id);
				continue;
			}

			const settlements = subsets[0];

			if (!settlements) {
				continue;
			}

			groups.push(reconciledGroup([credit], settlements, "GUARDIAN_GROUP"));
			removeMatched([credit], settlements);
		}

		const latestGuardianDate = guardianSettlements
			.map((settlement) => settlement.settledOn)
			.toSorted()
			.at(-1);

		for (const credit of availableCredits.values()) {
			if (ambiguousCredits.has(credit.id)) {
				groups.push(unmatchedGroup(credit, "REVIEW_REQUIRED", "MULTIPLE_GUARDIAN_COMBINATIONS"));
				continue;
			}

			groups.push(
				unmatchedGroup(
					credit,
					latestGuardianDate && credit.occurredOn >= latestGuardianDate
						? "AWAITING_GUARDIAN"
						: "REVIEW_REQUIRED",
					"NO_GUARDIAN_SETTLEMENT",
				),
			);
		}

		const orderedGroups = groups.toSorted((left, right) => {
			const byDate = right.occurredOn.localeCompare(left.occurredOn);

			return byDate || right.amountCents - left.amountCents || left.id.localeCompare(right.id);
		});

		return {
			summary: {
				total: summarizeGroups(orderedGroups),
				reconciled: summarizeGroups(orderedGroups.filter((group) => group.status === "RECONCILED")),
				awaitingGuardian: summarizeGroups(
					orderedGroups.filter((group) => group.status === "AWAITING_GUARDIAN"),
				),
				reviewRequired: summarizeGroups(
					orderedGroups.filter((group) => group.status === "REVIEW_REQUIRED"),
				),
				notReceivable: summarizeGroups(
					orderedGroups.filter((group) => group.status === "NOT_RECEIVABLE"),
				),
				guardian: {
					total: summarizeSettlements(guardianSettlements),
					reconciled: summarizeSettlements(
						guardianSettlements.filter((settlement) => !availableSettlements.has(settlement.id)),
					),
					unmatched: summarizeSettlements([...availableSettlements.values()]),
				},
			},
			groups: orderedGroups,
			unmatchedSettlements: [...availableSettlements.values()].toSorted((left, right) => {
				const byDate = right.settledOn.localeCompare(left.settledOn);

				return byDate || right.amountCents - left.amountCents || left.id.localeCompare(right.id);
			}),
		};

		function removeMatched(
			matchingCredits: BankCredit[],
			matchingSettlements: GuardianReceivableSettlement[],
		) {
			for (const credit of matchingCredits) {
				availableCredits.delete(credit.id);
			}

			for (const settlement of matchingSettlements) {
				availableSettlements.delete(settlement.id);
			}
		}
	},

	classifications(groups: ReceiptGroup[]) {
		return groups.flatMap((group): ReceiptMovementClassification[] => {
			if (group.status === "RECONCILED") {
				return group.bankTransactions.map((transaction) => ({
					transactionId: transaction.id,
					explanation: "Recebimento conciliado com o contas a receber do Guardian",
					destination: "Recebimento de cliente",
					basis: "GUARDIAN_RECONCILIATION",
				}));
			}

			if (group.status !== "NOT_RECEIVABLE") {
				return [];
			}

			switch (group.reason) {
				case "INTERNAL_TRANSFER":
					return group.bankTransactions.map((transaction) => ({
						transactionId: transaction.id,
						explanation: "Transferência identificada entre contas próprias da SINDI",
						destination: "Transferência entre contas próprias",
						basis: "BANK_DESCRIPTION_RULE",
					}));
				case "INVESTMENT_MOVEMENT":
					return group.bankTransactions.map((transaction) => ({
						transactionId: transaction.id,
						explanation: "Movimento identificado de aplicação financeira",
						destination: /^REND PAGO\b/u.test(transaction.description.trim())
							? "Rendimento financeiro"
							: "Movimento de aplicação financeira",
						basis: "BANK_DESCRIPTION_RULE",
					}));
				default:
					return [];
			}
		});
	},
};

function nonReceivableReason(description: string) {
	const normalized = description.trim();

	if (/^PIX TRANSF SINDI C\d{2}\/\d{2}$/u.test(normalized)) {
		return "INTERNAL_TRANSFER" as const;
	}

	if (/^(REND PAGO|RESG) APLI?C? AUT/u.test(normalized)) {
		return "INVESTMENT_MOVEMENT" as const;
	}

	return null;
}

function reconciledGroup(
	bankTransactions: BankCredit[],
	settlements: GuardianReceivableSettlement[],
	matchKind: MatchKind,
): ReceiptGroup {
	return {
		id: `RECONCILED:${bankTransactions
			.map((transaction) => transaction.id)
			.toSorted()
			.join(":")}`,
		status: "RECONCILED",
		matchKind,
		reason: null,
		occurredOn: bankTransactions[0].occurredOn,
		amountCents: bankTransactions.reduce(
			(total, transaction) => total + transaction.amountCents,
			0,
		),
		bankTransactions,
		settlements,
	};
}

function unmatchedGroup(
	credit: BankCredit,
	status: Exclude<ReceiptStatus, "RECONCILED">,
	reason: Exclude<ReceiptGroup["reason"], null>,
): ReceiptGroup {
	return {
		id: `${status}:${credit.id}`,
		status,
		matchKind: null,
		reason,
		occurredOn: credit.occurredOn,
		amountCents: credit.amountCents,
		bankTransactions: [credit],
		settlements: [],
	};
}

function matchingSubsets(settlements: GuardianReceivableSettlement[], target: number) {
	const ordered = settlements.toSorted(
		(left, right) => right.amountCents - left.amountCents || left.id.localeCompare(right.id),
	);
	const matches: GuardianReceivableSettlement[][] = [];

	search(0, 0, []);

	return matches;

	function search(index: number, total: number, selected: GuardianReceivableSettlement[]) {
		if (matches.length > 1 || total > target) {
			return;
		}

		if (total === target) {
			if (selected.length > 1) {
				matches.push(selected);
			}

			return;
		}

		for (let position = index; position < ordered.length; position += 1) {
			const settlement = ordered[position];

			search(position + 1, total + settlement.amountCents, [...selected, settlement]);

			if (matches.length > 1) {
				return;
			}
		}
	}
}

function matchKey(item: { occurredOn: string; amountCents: number }) {
	return `${item.occurredOn}:${item.amountCents}`;
}

function compareCredits(left: BankCredit, right: BankCredit) {
	const byDate = left.occurredOn.localeCompare(right.occurredOn);

	return byDate || right.amountCents - left.amountCents || left.id.localeCompare(right.id);
}

function summarizeGroups(groups: ReceiptGroup[]) {
	return {
		count: groups.reduce((total, group) => total + group.bankTransactions.length, 0),
		amountCents: groups.reduce((total, group) => total + group.amountCents, 0),
	};
}

function summarizeSettlements(settlements: GuardianReceivableSettlement[]) {
	return {
		count: settlements.length,
		amountCents: settlements.reduce((total, settlement) => total + settlement.amountCents, 0),
	};
}
